import { jsPDF } from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { Facture, Devis, Societe, Client } from "@/types";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
    translitterer,
    paysPourPdf,
    couvertParLaPoliceUnicode,
    type Script,
} from "@/lib/translitteration";
import {
    POLICE_UNICODE,
    OPTIONS_RTL,
    installerPoliceUnicode,
    policeUnicodeDisponible,
} from "@/lib/pdf-polices";
import {
    lireSnapshotClient,
    lireSnapshotSociete,
    scriptDuSnapshot,
    estEncoreBrouillon,
    type SnapshotClient,
    type SnapshotSociete,
} from "@/lib/facture-snapshot";
import { clientPourLeDocument } from "@/lib/adresses-facturation";

/**
 * LE SNAPSHOT NE FAIT AUTORITÉ QUE SUR UN DOCUMENT CLOS.
 *
 * Sur une facture émise, il EST le document : c'est tout l'objet du chantier.
 * Sur un brouillon, il n'est qu'un cache — l'application le réécrit à chaque
 * enregistrement — et un cache qu'on préfère à la source est un cache qui
 * ment. Le scénario est banal : un brouillon enregistré hier, l'adresse du
 * client corrigée aujourd'hui, l'aperçu rouvert sans réenregistrer. Sans ce
 * garde, l'aperçu montrait l'ANCIENNE adresse, et le brouillon cessait de
 * suivre la fiche — ce que la règle affichée promet pourtant.
 *
 * Constaté le 9 septembre 2026 en préparant le test d'immuabilité en
 * préproduction : la reprise avait posé un snapshot sur les brouillons, qui
 * devenaient sourds à toute modification jusqu'au prochain enregistrement.
 */
function snapshotsFaisantFoi(document: unknown): {
    client: SnapshotClient | null;
    societe: SnapshotSociete | null;
} {
    const d = (document ?? {}) as {
        statut?: string | null; isLocked?: boolean | null;
        snapshotGeleLe?: Date | string | null;
        snapshotClient?: string | null; snapshotSociete?: string | null;
    };
    if (estEncoreBrouillon(d)) return { client: null, societe: null };
    return {
        client: lireSnapshotClient(d.snapshotClient),
        societe: lireSnapshotSociete(d.snapshotSociete),
    };
}

/**
 * CE QUI SERA RÉELLEMENT ÉCRIT, ET AVEC QUELLE POLICE.
 *
 * Trois issues, dans cet ordre :
 *
 *  1. une version latine existe — calculée à l'écriture et rangée en base pour
 *     les clients, calculée à la volée pour la société émettrice, qui n'a pas
 *     ces colonnes. On l'imprime en « helvetica », comme le reste du document ;
 *  2. aucune romanisation n'est sortie, mais DejaVu sait dessiner l'écriture :
 *     on imprime l'ORIGINAL avec elle, réordonné s'il se lit de droite à
 *     gauche ;
 *  3. ni l'un ni l'autre — un nom entièrement idéographique, DejaVu ne portant
 *     aucun idéogramme : on l'ANNONCE. Le seul choix qui reste interdit est
 *     d'écrire une ligne vide, qui passerait les « filter(Boolean) » sans que
 *     personne ne voie jamais qu'il manque un destinataire.
 */
interface Rendu {
    texte: string;
    unicode: boolean;
    rtl: boolean;
}

const RIEN: Rendu = { texte: "", unicode: false, rtl: false };

/**
 * Annonce faite sur le document quand ni la romanisation ni la police ne
 * peuvent rendre le texte. Courte, entre crochets, et renvoyant là où la
 * valeur exacte se trouve — la fiche, qui la garde intacte.
 */
const MARQUEUR = "[écriture non transcrite \u2014 voir la fiche client]";

function rendre(latinStocke: string | null | undefined, original: string | null | undefined): Rendu {
    const romanise = latinStocke?.trim();
    if (romanise) return { texte: romanise, unicode: false, rtl: false };

    const source = (original ?? "").trim();
    if (!source) return RIEN;

    const t = translitterer(source);
    if (t.qualite !== "impossible") return { texte: t.latin, unicode: false, rtl: false };

    if (policeUnicodeDisponible() && couvertParLaPoliceUnicode(t.script)) {
        return { texte: t.original, unicode: true, rtl: t.rtl };
    }
    return { texte: MARQUEUR, unicode: false, rtl: false };
}

/** L'écriture d'origine, à imprimer SOUS le nom latin. Vide s'il n'y a rien à
 *  montrer de plus, ou si la police ne sait pas la dessiner. */
function renduOriginal(
    original: string | null | undefined,
    latin: string,
    script: Script | undefined
): Rendu {
    const source = (original ?? "").trim();
    if (!source || source === latin) return RIEN;
    if (!script || script === "latin") return RIEN;
    if (!policeUnicodeDisponible() || !couvertParLaPoliceUnicode(script)) return RIEN;
    return { texte: source, unicode: true, rtl: script === "arabe" || script === "hebreu" };
}

/**
 * Écrit un rendu, en basculant de police si besoin, et rend l'ordonnée
 * suivante. Les options bidirectionnelles ne sont passées QUE pour les
 * écritures de droite à gauche : les passer partout retournerait les lignes
 * latines.
 */
function ecrire(
    doc: jsPDF,
    rendu: Rendu,
    police: string,
    style: string,
    x: number,
    y: number,
    largeur: number,
    hauteurLigne: number
): number {
    if (!rendu.texte) return y;

    if (rendu.unicode) doc.setFont(POLICE_UNICODE, "normal");
    const lignes = doc.splitTextToSize(rendu.texte, largeur);
    doc.text(lignes, x, y, rendu.rtl ? { ...OPTIONS_RTL } : undefined);
    if (rendu.unicode) doc.setFont(police, style);

    return y + lignes.length * hauteurLigne;
}

/**
 * La société émettrice n'a pas de colonnes latines : elle est française dans
 * tous les cas constatés, et lui en ajouter serait une migration de plus pour
 * un besoin qui ne s'est pas présenté. Sa romanisation se calcule donc ici.
 */
const lisible = (valeur?: string | null): string => rendre(null, valeur).texte;

/**
 * Une valeur venue d'un SNAPSHOT s'imprime telle quelle.
 *
 * Elle a été romanisée au moment du gel : la retraiter ici la ferait dépendre
 * du code d'AUJOURD'HUI, et une facture figée changerait au premier correctif
 * d'une table de translittération. C'est exactement ce que le gel interdit.
 */
const renduDuSnapshot = (texte: string): Rendu => ({ texte, unicode: false, rtl: false });

function formatPaymentTerms(conditions: string | null | undefined): string {
    if (!conditions) return "Paiement à réception";

    const trimmed = conditions.trim();

    if (trimmed === "0 jour" || trimmed === "0 jours") {
        return "Paiement à réception";
    }

    if (trimmed.startsWith("+")) {
        return trimmed.substring(1).trim();
    }

    return trimmed;
}

function safeDate(value?: string | Date | null): string {
    if (!value) return "";
    try {
        return format(new Date(value), "dd/MM/yyyy", { locale: fr });
    } catch {
        return "";
    }
}

function formatMoney(value?: number | null): string {
    const amount = typeof value === "number" && !Number.isNaN(value) ? value : 0;
    return `${amount.toFixed(2).replace(".", ",")} €`;
}

function formatQty(value?: number | null): string {
    const amount = typeof value === "number" && !Number.isNaN(value) ? value : 0;
    return amount.toFixed(2).replace(".", ",");
}

function formatPercent(value?: number | null, decimals = 2): string {
    const amount = typeof value === "number" && !Number.isNaN(value) ? value : 0;
    const rounded = Math.round(amount);
    if (Math.abs(amount - rounded) < 0.00001) {
        return `${rounded} %`;
    }
    return `${amount.toFixed(decimals).replace(".", ",")} %`;
}

function cleanHtml(input?: string | null): string {
    return (input || "").replace(/<[^>]*>?/gm, "").trim();
}

function addWrappedText(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
): number {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + lines.length * lineHeight;
}

function drawFooter(
    doc: jsPDF,
    societe: Societe,
    pageWidth: number,
    pageHeight: number,
    marginLeft: number,
    marginRight: number,
    contentWidth: number,
    /** Les coordonnées figées, quand la facture en porte. Le pied de page
     *  répète l'identité de l'émetteur : il doit être figé comme le reste. */
    snapSociete?: SnapshotSociete | null
) {
    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const footerY = pageHeight - 16;
        const lineY = footerY - 5;

        doc.setDrawColor(232, 232, 232);
        doc.setLineWidth(0.2);
        doc.line(marginLeft, lineY, pageWidth - marginRight, lineY);

        const nom = snapSociete ? snapSociete.nom : lisible(societe.nom);
        const forme = (snapSociete ? snapSociete.formeJuridique : lisible(societe.formeJuridique)) || "SAS";
        const adresse = snapSociete
            ? snapSociete.adresse.join(" - ")
            : [
                  lisible(societe.adresse),
                  [lisible(societe.codePostal), lisible(societe.ville)].filter(Boolean).join(" "),
              ]
                  .filter(Boolean)
                  .join(" - ");

        const identifiants = snapSociete ?? societe;
        const line1 = [nom, forme, adresse].filter(Boolean).join(" - ");
        const line2 = [
            identifiants.siret ? `SIRET ${identifiants.siret}` : "",
            identifiants.tvaIntra ? `TVA ${identifiants.tvaIntra}` : "",
            identifiants.rcs ? `RCS ${identifiants.rcs}` : "",
        ]
            .filter(Boolean)
            .join(" - ");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(122, 122, 122);

        let currentFooterY = footerY - 1.5;

        // MENTIONS LÉGALES (Pied de page personnalisé) OU Pied de page automatique
        const mentionsLegales = snapSociete
            ? snapSociete.mentionsLegales
            : societe.mentionsLegales;
        if (mentionsLegales) {
            const lines = doc.splitTextToSize(mentionsLegales, contentWidth);
            doc.text(lines, pageWidth / 2, currentFooterY, { align: "center" });
            currentFooterY += lines.length * 3.5; // Espacement dynamique
        } else {
            // Si pas de mentions personnalisées, on affiche le bloc automatique
            if (line1) {
                doc.text(line1, pageWidth / 2, currentFooterY, { align: "center" });
                currentFooterY += 3.5;
            }
            if (line2) {
                doc.text(line2, pageWidth / 2, currentFooterY, { align: "center" });
            }
        }

        doc.text(`${i}/${pageCount}`, pageWidth - marginRight, footerY + 1.8, {
            align: "right",
        });
    }
}

type InvoiceRow = {
    description: string;
    date?: string;
    quantite: string;
    prixUnitaire: string;
    totalHT: string;
    tva: string;
    totalTTC?: string;
    remise?: string;
    isItemRow?: boolean;
    isTotalRow?: boolean;
    isGrandTotal?: boolean;
    isMinorTotal?: boolean;
};

export const generateInvoicePDF = (
    document: Facture | Devis,
    societe: Societe,
    clientVivant: Client,
    options?: {
        returnBlob?: boolean;
        returnBase64?: boolean;
    }
) => {
    try {
        // L'ADRESSE DE FACTURATION VISÉE PAR LE DOCUMENT.
        //
        // Une facture peut désigner une autre adresse que la principale du
        // client (« adresseFacturationId »). Plutôt que d'en instruire chaque
        // bloc du générateur, on lui donne une fiche dont l'adresse EST celle
        // du document — voir « src/lib/adresses-facturation.ts ». Sur un
        // document clos, le snapshot fait foi de toute façon et l'a déjà
        // figée ; c'est le brouillon, et le devis, que cette ligne sert.
        const client = clientVivant
            ? clientPourLeDocument(clientVivant, (document as any)?.adresseFacturationId)
            : clientVivant;

        if (!document || !societe || !client) {
            console.error("[PDF] Missing required data:", {
                document: !!document,
                societe: !!societe,
                client: !!client,
            });
            throw new Error("Données manquantes pour générer le PDF");
        }

        if (!document.numero) {
            console.error("[PDF] Missing document number");
            throw new Error("Numéro de document manquant");
        }

        if (document.config && typeof document.config === "string") {
            try {
                document.config = JSON.parse(document.config);
            } catch {
                document.config = {};
            }
        }

        if (!document.config || typeof document.config !== "object") {
            document.config = {};
        }

        const doc = new jsPDF({ unit: "mm", format: "a4" });

        // Sans effet si la police n'a pas été préchargée : le document
        // s'imprimera alors avec les seules romanisations, et annoncera les
        // rares champs qu'il n'aura pas su écrire. Jamais d'échec ici.
        installerPoliceUnicode(doc);

        const PAGE_WIDTH = 210;
        const PAGE_HEIGHT = 297;
        const MARGIN_LEFT = 18;
        const MARGIN_RIGHT = 18;
        const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;
        const FONT = "helvetica";

        const COLOR_TEXT: [number, number, number] = [26, 26, 26];
        const COLOR_MUTED: [number, number, number] = [118, 118, 118];
        const COLOR_LINE: [number, number, number] = [232, 232, 232];
        const COLOR_LINE_STRONG: [number, number, number] = [216, 216, 216];
        const COLOR_HEADER_BG: [number, number, number] = [247, 247, 247];

        // « FACTURE » VEUT DIRE FACTURE, quelle que soit la casse.
        //
        // La comparaison était stricte — « document.type === "Facture" ». Or
        // l'écran mobile nomme ses documents en capitales : « FACTURE » n'est
        // pas « Facture », la condition tombait à faux, et l'aperçu mobile
        // d'une facture s'imprimait en DEVIS. L'appelant a été corrigé ; le
        // générateur l'est aussi, parce qu'un document qui se déclare FACTURE
        // ne doit en aucun cas ressortir en devis.
        const typeDuDocument = String((document as any).type ?? "").toLowerCase();
        const isFacture = typeDuDocument ? typeDuDocument !== "devis" : true;

        const documentTitle = isFacture ? "FACTURE" : "DEVIS";
        const dateEmission = safeDate(document.dateEmission);
        const dateEcheance = isFacture
            ? safeDate((document as any).echeance)
            : safeDate((document as any).dateValidite);

        const opType = document.config?.operationType || "service";
        const operationLabel =
            opType === "none"
                ? ""
                : opType === "goods"
                    ? "Vente de biens"
                    : "Prestation de services";

        const showDateColumn = Boolean(document.config?.showDateColumn);
        const showTTCColumn = Boolean(document.config?.showTTCColumn);
        const showRemiseColumn = Boolean(document.config?.discountEnabled);

        const items = Array.isArray(document.items) ? document.items : [];

        // =========================================================
        // HEADER
        // =========================================================
        const topY = 15;
        let logoBottomY = topY;

        if (societe.logoUrl) {
            try {
                // Taille logo configurable depuis Paramètres > PDF
                const societeConfig = (() => { try { return societe.config ? (typeof societe.config === 'string' ? JSON.parse(societe.config) : societe.config) : {}; } catch { return {}; } })();
                const maxW = societeConfig.pdfLogoSize ?? 75;
                const maxH = Math.round(maxW * 0.47); // ratio hauteur proportionnel
                const props = doc.getImageProperties(societe.logoUrl);
                const ratio = props.width / props.height;

                let logoW = maxW;
                let logoH = logoW / ratio;

                if (logoH > maxH) {
                    logoH = maxH;
                    logoW = logoH * ratio;
                }

                // TEST: Forçage en JPEG pour tester si la transparence du PNG cause le glitch
                doc.addImage(societe.logoUrl, "JPEG", MARGIN_LEFT, topY, logoW, logoH, undefined, "FAST");
                logoBottomY = topY + logoH;
            } catch {
                // Ignore logo errors to avoid blocking PDF generation
            }
        }

        const rightX = PAGE_WIDTH - MARGIN_RIGHT;
        let headerRightY = topY + 5.5;

        doc.setFont(FONT, "bold");
        doc.setFontSize(20);
        doc.setTextColor(...COLOR_TEXT);
        doc.text(documentTitle, rightX, headerRightY, { align: "right" });

        headerRightY += 7;
        doc.setFont(FONT, "normal");
        doc.setFontSize(10);
        doc.setTextColor(...COLOR_TEXT);
        doc.text(`${isFacture ? "Facture" : "Devis"} n\u00b0${document.numero}`, rightX, headerRightY, { align: "right" });

        if (dateEmission) {
            headerRightY += 5;
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_MUTED);
            doc.text(`${isFacture ? "Date de facturation" : "Date du devis"} : ${dateEmission}`, rightX, headerRightY, {
                align: "right",
            });
        }

        const isOnlinePayment = 
            (document as any).conditions === "Paiement en ligne" || 
            (document as any).conditionsPaiement === "Paiement en ligne" ||
            !!(document as any).stripeInvoiceId;

        if (dateEcheance && !isOnlinePayment) {
            headerRightY += 4.5;
            doc.text(
                `${isFacture ? "Échéance" : "Validité"} : ${dateEcheance}`,
                rightX,
                headerRightY,
                { align: "right" }
            );
        }

        if (operationLabel) {
            headerRightY += 4.5;
            doc.text(operationLabel, rightX, headerRightY, { align: "right" });
        }

        const headerBottomY = Math.max(logoBottomY, headerRightY) + 8;

        doc.setDrawColor(...COLOR_LINE);
        doc.setLineWidth(0.15);
        doc.line(MARGIN_LEFT, headerBottomY, PAGE_WIDTH - MARGIN_RIGHT, headerBottomY);

        // =========================================================
        // PARTIES
        // =========================================================
        const colGap = 16;
        const colWidth = (CONTENT_WIDTH - colGap) / 2;
        const leftColX = MARGIN_LEFT;
        const rightColX = MARGIN_LEFT + colWidth + colGap;
        const addressStartY = headerBottomY + 10;

        let emitterY = addressStartY;
        doc.setFont(FONT, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MUTED);
        doc.text("ÉMETTEUR", leftColX, emitterY);

        emitterY += 6;
        doc.setFont(FONT, "bold");
        doc.setFontSize(11);
        doc.setTextColor(...COLOR_TEXT);
        // Même règle pour l'émetteur : le snapshot fait autorité. Changer de
        // siège social ne doit pas réécrire l'adresse d'une facture déjà émise.
        const snapSociete = snapshotsFaisantFoi(document).societe;

        emitterY = addWrappedText(
            doc,
            snapSociete ? snapSociete.nom : lisible(societe.nom),
            leftColX, emitterY, colWidth, 4.8
        );

        doc.setFont(FONT, "normal");
        doc.setFontSize(9);
        doc.setTextColor(79, 79, 79);

        const emitterLines = (
            snapSociete
                ? [...snapSociete.adresse, snapSociete.telephone ?? "", snapSociete.email ?? ""]
                : [
                      lisible(societe.adresse),
                      [lisible(societe.codePostal), lisible(societe.ville)].filter(Boolean).join(" "),
                      paysPourPdf(societe.pays) || "France",
                      societe.telephone || "",
                      societe.email || "",
                  ]
        ).filter(Boolean);

        emitterLines.forEach((line) => {
            if (line) emitterY = addWrappedText(doc, line, leftColX, emitterY, colWidth, 4.6);
        });

        let clientY = addressStartY;
        doc.setFont(FONT, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MUTED);
        doc.text("CLIENT", rightColX, clientY);

        clientY += 6;
        doc.setFont(FONT, "bold");
        doc.setFontSize(11);
        doc.setTextColor(...COLOR_TEXT);
        // Le NOM. La version latine vient de la base — calculée à l'écriture,
        // la même que celle affichée dans la liste et la fiche. Le PDF ne
        // romanise rien de son côté : deux romanisations pour un même client
        // seraient deux noms.
        // LE SNAPSHOT FAIT AUTORITÉ.
        //
        // S'il existe, la fiche client vivante n'est PLUS consultée : c'est tout
        // l'objet de ce chantier. Une facture émise porte les coordonnées du
        // jour de son émission, et corriger la fiche six mois plus tard ne doit
        // plus rien changer au document.
        //
        // Il n'y a de repli sur la fiche que pour une facture SANS snapshot —
        // un brouillon d'avant la reprise. Mieux vaut le comportement d'avant
        // qu'un bloc client vide.
        const snapshot = snapshotsFaisantFoi(document).client;

        const nomClient = snapshot
            ? renduDuSnapshot(snapshot.nom)
            : rendre(client.nomLatin, client.nom);
        clientY = ecrire(doc, nomClient, FONT, "bold", rightColX, clientY, colWidth, 4.8);

        // ... et SOUS lui, l'écriture d'origine, quand elle dit autre chose et
        // que DejaVu sait la dessiner. C'est ce qui permet au client de
        // reconnaître son propre nom sur la facture qu'il reçoit. Seul le nom
        // en bénéficie : doubler aussi les lignes d'adresse encombrerait le
        // bloc sans rien apprendre à personne.
        const nomOriginal = snapshot
            ? renduOriginal(snapshot.nomOriginal, nomClient.texte, scriptDuSnapshot(snapshot) as Script)
            : renduOriginal(client.nom, nomClient.texte, client.scriptOriginal as Script);
        if (nomOriginal.texte) {
            doc.setFontSize(9.5);
            doc.setTextColor(...COLOR_MUTED);
            clientY = ecrire(doc, nomOriginal, FONT, "bold", rightColX, clientY, colWidth, 4.6);
        }

        doc.setFont(FONT, "normal");
        doc.setFontSize(9);
        doc.setTextColor(79, 79, 79);

        // Le code postal n'a pas de colonne latine : il est numérique partout,
        // et le romaniser n'aurait aucun sens. Il passe tout de même par
        // « rendre », pour le pays qui l'écrirait en chiffres locaux.
        const ville = [
            rendre(null, client.codePostal).texte,
            rendre(client.villeLatin, client.ville).texte,
        ].filter(Boolean).join(" ");

        const clientLines: Rendu[] = snapshot
            ? snapshot.adresse.map(renduDuSnapshot)
            : [
                  rendre(client.adresseLatin, client.adresse),
                  rendre(client.adresse2Latin, client.adresse2),
                  ville ? { texte: ville, unicode: false, rtl: false } : RIEN,
                  { texte: paysPourPdf(client.pays), unicode: false, rtl: false },
              ].filter((r) => r.texte);

        clientLines.forEach((ligne) => {
            clientY = ecrire(doc, ligne, FONT, "normal", rightColX, clientY, colWidth, 4.6);
        });

        const sectionBottomY = Math.max(emitterY, clientY) + 10;

        // =========================================================
        // TABLE DATA
        // =========================================================
        const columns = [
            { header: "Description", dataKey: "description" },
            ...(showDateColumn ? [{ header: "Date", dataKey: "date" }] : []),
            { header: "Quantité", dataKey: "quantite" },
            { header: "Prix unitaire", dataKey: "prixUnitaire" },
            { header: "Total HT", dataKey: "totalHT" },
            { header: "TVA", dataKey: "tva" },
            ...(showTTCColumn ? [{ header: "Total TTC", dataKey: "totalTTC" }] : []),
            ...(showRemiseColumn ? [{ header: "Remise", dataKey: "remise" }] : []),
        ];

        const tableBody: InvoiceRow[] = items.map((item) => {
            const pu = typeof item.prixUnitaire === "number" ? item.prixUnitaire : 0;
            const qty = typeof item.quantite === "number" ? item.quantite : 1;
            const tva = typeof item.tva === "number" ? item.tva : 0;
            const remiseVal = item.remise || 0;
            const remiseType = item.remiseType || "pourcentage";

            let discountAmount = 0;
            if (remiseVal > 0) {
                discountAmount =
                    remiseType === "montant"
                        ? remiseVal
                        : pu * qty * (remiseVal / 100);
            }

            const montantHT = pu * qty - discountAmount;
            const montantTTC = montantHT * (1 + tva / 100);

            const row: InvoiceRow = {
                description: cleanHtml(item.description),
                quantite: formatQty(qty),
                prixUnitaire: formatMoney(pu),
                totalHT: formatMoney(montantHT),
                tva: formatPercent(tva),
                isItemRow: true,
            };

            if (showDateColumn) {
                row.date = item.date ? safeDate(item.date) : "";
            }
            if (showTTCColumn) {
                row.totalTTC = formatMoney(montantTTC);
            }
            if (showRemiseColumn) {
                row.remise =
                    remiseVal > 0
                        ? remiseType === "montant"
                            ? formatMoney(remiseVal)
                            : formatPercent(remiseVal, 0)
                        : "";
            }

            return row;
        });

        const itemsCount = tableBody.length;

        let totalLineDiscounts = 0;
        items.forEach((item) => {
            const pu = typeof item.prixUnitaire === "number" ? item.prixUnitaire : 0;
            const qty = typeof item.quantite === "number" ? item.quantite : 1;
            const remiseVal = item.remise || 0;
            const remiseType = item.remiseType || "pourcentage";
            const brut = pu * qty;

            if (remiseVal > 0) {
                totalLineDiscounts +=
                    remiseType === "montant" ? remiseVal : brut * (remiseVal / 100);
            }
        });

        // Les totaux sont désormais dessinés manuellement après le tableau
        // pour un contrôle parfait de l'espacement vertical.

        // =========================================================
        // TABLE LAYOUT
        // =========================================================
        const widthMap = {
            quantite: 18,
            prixUnitaire: 24,
            totalHT: 24,
            tva: 18,
            date: 22,
            totalTTC: 24,
            remise: 18,
        };

        const usedWidth =
            widthMap.quantite +
            widthMap.prixUnitaire +
            widthMap.totalHT +
            widthMap.tva +
            (showDateColumn ? widthMap.date : 0) +
            (showTTCColumn ? widthMap.totalTTC : 0) +
            (showRemiseColumn ? widthMap.remise : 0);

        const descriptionWidth = CONTENT_WIDTH - usedWidth;

        const columnStyles: Record<string, any> = {
            description: { cellWidth: descriptionWidth, halign: "left" },
            quantite: { cellWidth: widthMap.quantite, halign: "right" },
            prixUnitaire: { cellWidth: widthMap.prixUnitaire, halign: "right" },
            totalHT: { cellWidth: widthMap.totalHT, halign: "right" },
            tva: { cellWidth: widthMap.tva, halign: "right" },
        };

        if (showDateColumn) {
            columnStyles.date = { cellWidth: widthMap.date, halign: "left" };
        }
        if (showTTCColumn) {
            columnStyles.totalTTC = { cellWidth: widthMap.totalTTC, halign: "right" };
        }
        if (showRemiseColumn) {
            columnStyles.remise = { cellWidth: widthMap.remise, halign: "right" };
        }

        autoTable(doc, {
            startY: sectionBottomY,
            columns,
            body: tableBody as RowInput[],
            theme: "plain",
            margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT },
            styles: {
                font: FONT,
                fontSize: 9,
                textColor: COLOR_TEXT,
                cellPadding: {
                    top: 1.4,
                    bottom: 1.4,
                    left: 1.4,
                    right: 1.4,
                },
                lineWidth: 0,
                valign: "middle",
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: COLOR_HEADER_BG,
                textColor: [42, 42, 42],
                fontStyle: "bold",
                fontSize: 8.5,
                lineWidth: 0,
            },
            columnStyles,
            didParseCell: (data) => {
                const key = String(data.column.dataKey);

                if (data.section === "head") {
                    data.cell.styles.minCellHeight = 8.5;
                    data.cell.styles.fillColor = COLOR_HEADER_BG;
                    data.cell.styles.textColor = [42, 42, 42];
                    data.cell.styles.halign =
                        key === "description" || key === "date" ? "left" : "right";
                    return;
                }

                data.cell.styles.minCellHeight = 8.2;
                data.cell.styles.halign =
                    key === "description" || key === "date" ? "left" : "right";
            },
            didDrawCell: (data) => {
                const { cell, row, table } = data;

                if (data.section === "head") {
                    doc.setDrawColor(...COLOR_LINE_STRONG);
                    doc.setLineWidth(0.2);
                    doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
                    doc.line(
                        cell.x,
                        cell.y + cell.height,
                        cell.x + cell.width,
                        cell.y + cell.height
                    );
                    return;
                }

                if (data.section === "body") {
                    doc.setDrawColor(...COLOR_LINE);
                    doc.setLineWidth(0.15);
                    doc.line(
                        cell.x,
                        cell.y + cell.height,
                        cell.x + cell.width,
                        cell.y + cell.height
                    );

                    if (row.index === table.body.length - 1) {
                        doc.setDrawColor(...COLOR_LINE_STRONG);
                        doc.setLineWidth(0.2);
                        doc.line(
                            cell.x,
                            cell.y + cell.height,
                            cell.x + cell.width,
                            cell.y + cell.height
                        );
                    }
                }
            },
        });

        let currentY = ((doc as any).lastAutoTable?.finalY || sectionBottomY) + 4;

        if (currentY + 25 > PAGE_HEIGHT - 28) {
            doc.addPage();
            currentY = 20;
        }

        // =========================================================
        // TOTAUX MANUELS (Contrôle parfait de l'espacement)
        // =========================================================
        // Le bloc de totaux est toujours ancré au bord droit de la page,
        // quelle que soit la composition des colonnes du tableau.
        // labelX est à 18 mm à gauche de valueX (même écart qu'avant, désormais fixe).
        const valueX = PAGE_WIDTH - MARGIN_RIGHT - 1.4;
        const labelX = valueX - 18;

        // Remise globale : lue au niveau document, avec fallback dans config (pour
        // les anciennes factures où le champ aurait été stocké dans le JSON config).
        const remiseGlobaleValue = Number(
            (document as any).remiseGlobale ??
            (document.config as any)?.remiseGlobale ??
            0
        ) || 0;
        const remiseGlobaleType: "pourcentage" | "montant" = (
            (document as any).remiseGlobaleType ??
            (document.config as any)?.remiseGlobaleType ??
            "pourcentage"
        ) as "pourcentage" | "montant";

        // HT brut (avant toute remise) recalculé à partir des lignes pour ne pas
        // dépendre d'un éventuel champ stocké.
        const htBrutItems = items.reduce((sum, item: any) => {
            const qty = Number(item.quantite) || 0;
            const pu = Number(item.prixUnitaire) || 0;
            return sum + qty * pu;
        }, 0);

        const htApresRemiseLigne = Math.max(0, htBrutItems - totalLineDiscounts);

        // Calcul du montant de la remise globale.
        // On reconcile avec le totalHT stocké pour absorber les arrondis et rester
        // cohérent avec ce qui a été persisté.
        let remiseGlobaleMontant = 0;
        if (remiseGlobaleValue > 0) {
            if (remiseGlobaleType === "montant") {
                remiseGlobaleMontant = Math.min(remiseGlobaleValue, htApresRemiseLigne);
            } else {
                remiseGlobaleMontant = htApresRemiseLigne * (remiseGlobaleValue / 100);
            }
        } else {
            // Cas dégradé : pas de champ remiseGlobale mais le totalHT stocké est
            // plus bas que htApresRemiseLigne => on déduit qu'il y avait une remise.
            const delta = htApresRemiseLigne - (document.totalHT || 0);
            if (delta > 0.5) {
                remiseGlobaleMontant = delta;
            }
        }

        // Si une valeur a été saisie mais que le totalHT stocké la contredit
        // (ex: facture sauvegardée à l'ancienne sans recalcul), on fait confiance
        // au totalHT pour éviter d'afficher un solde incohérent.
        if (remiseGlobaleValue > 0) {
            const expectedHt = htApresRemiseLigne - remiseGlobaleMontant;
            if (Math.abs(expectedHt - (document.totalHT || 0)) > 0.5) {
                remiseGlobaleMontant = Math.max(0, htApresRemiseLigne - (document.totalHT || 0));
            }
        }

        const hasLineDiscounts = totalLineDiscounts > 0;
        const hasGlobalDiscount = remiseGlobaleMontant > 0.005;

        if (hasLineDiscounts || hasGlobalDiscount) {
            doc.setFont(FONT, "normal");
            doc.setFontSize(8.3);
            doc.setTextColor(...COLOR_MUTED);

            doc.text("Total HT brut", labelX, currentY, { align: "right" });
            doc.text(formatMoney(htBrutItems), valueX, currentY, { align: "right" });
            currentY += 4.5;

            if (hasLineDiscounts) {
                const lineDiscountLabel = hasGlobalDiscount ? "Remises sur lignes" : "Total remises";
                doc.text(lineDiscountLabel, labelX, currentY, { align: "right" });
                doc.text(`- ${formatMoney(totalLineDiscounts)}`, valueX, currentY, { align: "right" });
                currentY += 4.5;
            }

            if (hasGlobalDiscount) {
                const globalLabel = remiseGlobaleValue > 0 && remiseGlobaleType === "pourcentage"
                    ? `Remise globale (${formatPercent(remiseGlobaleValue, 2)})`
                    : "Remise globale";
                doc.text(globalLabel, labelX, currentY, { align: "right" });
                doc.text(`- ${formatMoney(remiseGlobaleMontant)}`, valueX, currentY, { align: "right" });
                currentY += 4.5;
            }

            currentY += 0.5;
        }

        // Read TVA exemption setting from societe config
        const societeConfig = (() => { try { return societe.config ? (typeof societe.config === 'string' ? JSON.parse(societe.config) : societe.config) : {}; } catch { return {}; } })();
        const isTvaExempt = Boolean(societeConfig.showTvaExemption);

        doc.setFont(FONT, "normal");
        doc.setFontSize(9);
        doc.setTextColor(85, 85, 85); // Couleur gris foncé pour le HT
        doc.text("Total HT", labelX, currentY, { align: "right" });
        doc.text(formatMoney(document.totalHT || 0), valueX, currentY, { align: "right" });

        currentY += 4.8; // Espacement modéré

        if (isTvaExempt) {
            // TVA non applicable — Article 293 B du CGI
            doc.setFont(FONT, "italic");
            doc.setFontSize(8);
            doc.setTextColor(112, 112, 112);
            doc.text("TVA non applicable, article 293 B du CGI", valueX, currentY, { align: "right" });

            currentY += 5.3;

            doc.setFont(FONT, "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_TEXT);
            doc.text("Total", labelX, currentY, { align: "right" });
            doc.text(formatMoney(document.totalHT || 0), valueX, currentY, { align: "right" });
        } else {
            const tvaAmount = (document.totalTTC || 0) - (document.totalHT || 0);
            const vatRate =
                document.totalHT && document.totalHT !== 0
                    ? (tvaAmount / document.totalHT) * 100
                    : 20;

            doc.text(`TVA ${formatPercent(vatRate)}`, labelX, currentY, { align: "right" });
            doc.text(formatMoney(tvaAmount), valueX, currentY, { align: "right" });

            currentY += 5.3; // Respiration avant le TTC

            doc.setFont(FONT, "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_TEXT);
            doc.text("Total TTC", labelX, currentY, { align: "right" });
            doc.text(formatMoney(document.totalTTC || 0), valueX, currentY, { align: "right" });
        }

        currentY += 10; // Espace avant bloc de paiement

        // =========================================================
        // PAYMENT
        // =========================================================
        if (currentY + 40 > PAGE_HEIGHT - 28) {
            doc.addPage();
            currentY = 20;
        }

        doc.setFont(FONT, "bold");
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_TEXT);
        doc.text("Paiement", MARGIN_LEFT, currentY);

        currentY += 6;
        doc.setFont(FONT, "normal");
        doc.setFontSize(9);
        doc.setTextColor(79, 79, 79);

        const paymentLines = [
            // Les coordonnées BANCAIRES sont figées elles aussi : elles disent
            // où payer, et changer de banque ne doit pas réécrire une facture
            // déjà partie avec l'ancien IBAN.
            (snapSociete?.banque ?? societe.banque) ? `Banque : ${snapSociete?.banque ?? societe.banque}` : "",
            (snapSociete?.bic ?? societe.bic) ? `SWIFT/BIC : ${snapSociete?.bic ?? societe.bic}` : "",
            (snapSociete?.iban ?? societe.iban) ? `IBAN : ${snapSociete?.iban ?? societe.iban}` : "",
            `Conditions : ${formatPaymentTerms(
                (("conditions" in document && document.conditions) ||
                ("conditionsPaiement" in (document as any) && (document as any).conditionsPaiement))
                    ? ("conditions" in document && document.conditions ? document.conditions : (document as any).conditionsPaiement)
                    : ((snapSociete?.conditionsParDefaut ?? societe.defaultConditions) || "30 jours")
            )}`,
        ].filter(Boolean);

        paymentLines.forEach((line) => {
            currentY = addWrappedText(doc, line, MARGIN_LEFT, currentY, CONTENT_WIDTH, 5);
        });

        const notesText = cleanHtml(document.notes || "");
        if (notesText) {
            currentY += 4;
            doc.setFont(FONT, "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_TEXT);
            doc.text("Notes", MARGIN_LEFT, currentY);

            currentY += 5;
            doc.setFont(FONT, "normal");
            doc.setFontSize(8);
            doc.setTextColor(112, 112, 112);
            currentY = addWrappedText(doc, notesText, MARGIN_LEFT, currentY, CONTENT_WIDTH, 4.3);
        }

        // =========================================================
        // LEGAL — Facture uniquement (pénalités de retard, indemnité 40€)
        // =========================================================
        if (isFacture) {
            const defaultRetard = "En cas de retard de paiement, des pénalités seront exigibles sans qu’un rappel soit nécessaire, conformément aux dispositions des articles L441-10 et D441-5 du Code de commerce. Le taux des pénalités de retard est fixé à une fois et demie le taux d’intérêt légal, applicable à compter du jour suivant la date de règlement figurant sur la facture.\n\nConformément aux articles L441-10 et D441-5 du Code de commerce, une indemnité forfaitaire pour frais de recouvrement d’un montant de 40 \u20ac sera également due de plein droit par le débiteur en cas de retard de paiement.";

            // Le texte de retard est CONTRACTUEL : il énonce des pénalités.
            // Celui qui figurait sur le document émis est celui qui engage.
            const retardText = (snapSociete ? snapSociete.mentionsRetard : societe.mentionsRetard) || defaultRetard;

            currentY += 8;

            if (currentY + 25 > PAGE_HEIGHT - 24) {
                doc.addPage();
                currentY = 20;
            }

            doc.setFont(FONT, "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLOR_TEXT);
            doc.text("Mentions légales", MARGIN_LEFT, currentY);

            currentY += 5.5;
            doc.setFont(FONT, "normal");
            doc.setFontSize(7.5);
            doc.setTextColor(112, 112, 112);
            currentY = addWrappedText(doc, retardText, MARGIN_LEFT, currentY, CONTENT_WIDTH, 4.1);
        } else {
            // =====================================================
            // DEVIS — Validité + Bloc "Bon pour accord"
            // =====================================================
            currentY += 8;

            if (currentY + 55 > PAGE_HEIGHT - 24) {
                doc.addPage();
                currentY = 20;
            }

            // Ligne de validité
            const validiteText = (document as any).dateValidite
                ? `Ce devis est valable jusqu’au ${safeDate((document as any).dateValidite)}.`
                : "Ce devis est valable 30 jours à compter de sa date d’émission.";

            doc.setFont(FONT, "normal");
            doc.setFontSize(8);
            doc.setTextColor(112, 112, 112);
            doc.text(validiteText, MARGIN_LEFT, currentY);

            currentY += 6;
        }

        // =========================================================
        // CGV PAGE
        // =========================================================
        const cgv = snapSociete ? snapSociete.cgv : societe.cgv;
        if (cgv) {
            doc.addPage();

            let yCGV = 20;
            doc.setFont(FONT, "bold");
            doc.setFontSize(11);
            doc.setTextColor(...COLOR_TEXT);
            doc.text("Conditions Générales de Vente", MARGIN_LEFT, yCGV);

            yCGV += 8;
            doc.setDrawColor(...COLOR_LINE);
            doc.setLineWidth(0.2);
            doc.line(MARGIN_LEFT, yCGV, PAGE_WIDTH - MARGIN_RIGHT, yCGV);

            yCGV += 8;
            doc.setFont(FONT, "normal");
            doc.setFontSize(7.4);
            doc.setTextColor(79, 79, 79);

            const cgvLines = doc.splitTextToSize(cgv, CONTENT_WIDTH);
            doc.text(cgvLines, MARGIN_LEFT, yCGV);
        }

        // =========================================================
        // WATERMARK
        // =========================================================
        if (document.statut === "Annulée" || document.statut === "Archivée") {
            const label =
                document.statut === "Annulée"
                    ? "FACTURE ANNULÉE"
                    : "FACTURE ARCHIVÉE";

            const color =
                document.statut === "Annulée"
                    ? ([230, 80, 80] as [number, number, number])
                    : ([160, 160, 160] as [number, number, number]);

            const pageCount = doc.getNumberOfPages();

            for (let p = 1; p <= pageCount; p++) {
                doc.setPage(p);

                const centerX = PAGE_WIDTH / 2;
                const centerY = PAGE_HEIGHT / 2;

                doc.saveGraphicsState();
                doc.setTextColor(...color);
                doc.setGState(new (doc as any).GState({ opacity: 0.16 }));
                doc.setFont(FONT, "bold");
                doc.setFontSize(48);
                doc.text(label, centerX, centerY, {
                    align: "center",
                    angle: 45,
                });
                doc.restoreGraphicsState();
            }
        }

        // =========================================================
        // FOOTER
        // =========================================================
        drawFooter(doc, societe, PAGE_WIDTH, PAGE_HEIGHT, MARGIN_LEFT, MARGIN_RIGHT, CONTENT_WIDTH, snapSociete);

        // =========================================================
        // OUTPUT
        // =========================================================
        if (options?.returnBlob) {
            return doc.output("bloburl");
        }

        if (options?.returnBase64) {
            const dataUri = doc.output("datauristring");
            return dataUri.split(",")[1];
        }

        // Le nom du fichier passe par la même translittération : le filtre
        // ci-dessous ne garde que du latin, et une raison sociale cyrillique
        // s'y réduisait à un unique « _ ».
        const safeNom = lisible(societe.nom).replace(/[^a-zA-Z0-9À-ÿ_-]/g, "_").replace(/_+/g, "_");
        doc.save(`${isFacture ? "Facture" : "Devis"}_${safeNom}_${document.numero}.pdf`);
    } catch (error) {
        console.error("PDF generation error", error);
    }
};
